// 가져오기 요약 — 담을 합계 · 「담지 않음」 합계 · 원본 대조 경고.

import { formatAmount } from "../../../lib/format";
import type { VatMode } from "../../money/vat";
import { convertImportPrices, totalOf } from "./rows";
import { SKIP_TRADE } from "./trade-match";
import { ImportParse } from "./types";

/** 원본 합계와 이 비율 넘게 차이 나면 경고한다. */
export const DIFF_WARN_RATIO = 0.01;

export interface ImportSummary {
  /** 담을 공종(공종을 고른 것만 — 「N줄 담기」 와 같은 기준)의 합계 · 단가를 견적 방식으로 바꾼 뒤 값. */
  pickedTotal: number | null;
  /** 아직 공종을 고르지 않은 덩이의 줄 수·합계 — 고르면 담긴다. */
  pendingRows: number;
  pendingTotal: number | null;
  /** 「담지 않음」 공종의 줄 수·합계. */
  skippedRows: number;
  skippedTotal: number | null;
  /** 읽은 전체 합계 — 원본 대조 기준. */
  readTotal: number | null;
  originalTotal: number | null;
  /** 읽은 전체 − 원본. 대조할 수 없으면 null. */
  diff: number | null;
  /** 계산 합계가 0인데 원본이 있거나, 차이가 원본의 1% 를 넘는다. */
  warn: boolean;
}

/** 파싱 결과와 공종 짝짓기로 요약을 만든다. 원본 대조는 일부러 뺀 공종까지 더한 읽은 합계로 한다. */
export const summarizeImport = (
  parsed: Pick<ImportParse, "groups" | "originalTotal" | "computedTotal">,
  assign: Record<string, string>,
  // 원본 단가 기준 → 견적 방식 — 담을·고르면 더할 합계만 바꾼 단가로 센다(원본 대조는 원본 기준 그대로).
  vat?: { from: VatMode; to: VatMode }
): ImportSummary => {
  const skipped = parsed.groups.filter((g) => assign[g.name] === SKIP_TRADE);
  const pending = parsed.groups.filter((g) => !assign[g.name]);
  const kept = parsed.groups.filter((g) => assign[g.name] && assign[g.name] !== SKIP_TRADE);
  const readTotal = parsed.computedTotal;
  const original = parsed.originalTotal;
  const diff = original !== null && readTotal !== null ? readTotal - original : null;
  const warn =
    original !== null &&
    original > 0 &&
    readTotal !== null &&
    (readTotal === 0 || Math.abs(readTotal - original) > original * DIFF_WARN_RATIO);
  const converted = (groups: typeof kept) => (vat ? convertImportPrices(groups, vat.from, vat.to) : groups);
  return {
    pickedTotal: totalOf(converted(kept).flatMap((g) => g.items)),
    pendingRows: pending.reduce((n, g) => n + g.items.length, 0),
    pendingTotal: totalOf(converted(pending).flatMap((g) => g.items)),
    skippedRows: skipped.reduce((n, g) => n + g.items.length, 0),
    skippedTotal: totalOf(skipped.flatMap((g) => g.items)),
    readTotal,
    originalTotal: original,
    diff,
    warn,
  };
};

// 「공종을 고르면 +N원」 문구 — 합계를 믿을 수 없으면(null) +0원 대신 문구를 뺀다.
export const pendingNoteOf = (summary: Pick<ImportSummary, "pendingRows" | "pendingTotal">): string | null =>
  summary.pendingRows > 0 && summary.pendingTotal !== null
    ? ` · 공종을 고르면 +${formatAmount(summary.pendingTotal)}원`
    : null;

// 「담지 않음」 문구 — 합계를 믿을 수 없으면(null) 0원 대신 금액을 뺀다.
export const skippedNoteOf = (summary: Pick<ImportSummary, "skippedRows" | "skippedTotal">): string | null => {
  if (summary.skippedRows === 0) return null;
  const amount = summary.skippedTotal === null ? "" : ` · ${formatAmount(summary.skippedTotal)}원`;
  return `담지 않음 ${summary.skippedRows}줄${amount}`;
};
